import { Box, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import DOMPurify from 'dompurify';
import { OnboardingStepData } from './useOnboardingSteps';
import { openExtensionPage } from '@/utils/extensionCommunication';
import {
  StepCard,
  DecorativeOrb,
  GradientTitle,
  ContentContainer,
  MediaImage,
  VideoContainer,
  CtaButton,
  BackButton,
  GradientButton,
} from './styles';

interface OnboardingStepProps {
  step: OnboardingStepData;
  stepIndex: number;
  totalSteps: number;
  isLastStep: boolean;
  isBusy?: boolean;
  onBack?: () => void;
  onNext: () => void;
  onCtaAction?: (action: string) => void;
  direction?: 'forward' | 'backward';
}

const isImageUrl = (url: string) => /\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(url);
const isEmbedVideoUrl = (url: string) => /(youtube\.com|youtu\.be|vimeo\.com|player\.vimeo\.com)/i.test(url);

const isAllowedMediaProtocol = (url: string) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Converts video URLs to their embeddable format.
 * - Vimeo: https://vimeo.com/123456789 -> https://player.vimeo.com/video/123456789
 * - YouTube: https://youtube.com/watch?v=abc -> https://youtube.com/embed/abc
 * - YouTube short: https://youtu.be/abc -> https://youtube.com/embed/abc
 */
const toEmbedUrl = (url: string): string => {
  try {
    const parsed = new URL(url);

    // Vimeo: convert vimeo.com/ID to player.vimeo.com/video/ID
    if (parsed.hostname === 'vimeo.com' || parsed.hostname === 'www.vimeo.com') {
      const videoId = parsed.pathname.replace(/^\//, '').split('/')[0];
      if (videoId && /^\d+$/.test(videoId)) {
        return `https://player.vimeo.com/video/${videoId}`;
      }
    }

    // YouTube: convert watch?v=ID to embed/ID
    if (parsed.hostname === 'youtube.com' || parsed.hostname === 'www.youtube.com') {
      const videoId = parsed.searchParams.get('v');
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }

    // YouTube short URLs: convert youtu.be/ID to embed/ID
    if (parsed.hostname === 'youtu.be') {
      const videoId = parsed.pathname.replace(/^\//, '');
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }

    // Already an embed URL or other format, return as-is
    return url;
  } catch {
    return url;
  }
};

const getMediaUrl = (step: OnboardingStepData) => {
  const url = step.media ?? step.mediaUrl ?? step.media_url;
  if (!url) {
    return undefined;
  }
  return isAllowedMediaProtocol(url) ? url : undefined;
};

/**
 * Converts iframe src URLs within HTML content to embeddable format.
 * This handles cases where non-embed URLs were saved in the content.
 */
const convertEmbedUrlsInHtml = (html: string): string => {
  // Match iframe src attributes and convert video URLs
  return html.replace(/(<iframe[^>]*\ssrc=["'])([^"']+)(["'][^>]*>)/gi, (_match, before, url, after) => {
    const convertedUrl = toEmbedUrl(url);
    return `${before}${convertedUrl}${after}`;
  });
};

const slideVariants = {
  enter: (direction: 'forward' | 'backward') => ({
    x: direction === 'forward' ? 1000 : -1000,
    opacity: 0,
    scale: 0.8,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
  },
  exit: (direction: 'forward' | 'backward') => ({
    x: direction === 'forward' ? -1000 : 1000,
    opacity: 0,
    scale: 0.8,
  }),
};

const OnboardingStep = ({
  step,
  stepIndex,
  isLastStep,
  isBusy,
  onBack,
  onNext,
  onCtaAction,
  direction = 'forward',
}: OnboardingStepProps) => {
  const mediaUrl = getMediaUrl(step);
  const showBack = Boolean(onBack) && stepIndex > 0;
  const showCta = Boolean(step.ctaLabel && step.ctaAction && onCtaAction);

  // Handle clicks on extension links within the content
  const handleContentClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const link = target.closest('a[data-extension-path]') as HTMLAnchorElement | null;

    if (link) {
      event.preventDefault();
      const extensionPath = link.getAttribute('data-extension-path');
      const openInNewTab = link.getAttribute('target') === '_blank';

      if (extensionPath) {
        openExtensionPage(extensionPath, openInNewTab);
      }
    }
  };

  return (
    <motion.div
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{
        x: { type: 'spring', stiffness: 300, damping: 30 },
        opacity: { duration: 0.3 },
        scale: { duration: 0.3 },
      }}
      style={{
        position: 'absolute',
        width: '100%',
      }}
    >
      <StepCard>
        <DecorativeOrb />

        <Stack spacing={1}>
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <Stack spacing={2}>
              <GradientTitle variant="h3">{step.title}</GradientTitle>
            </Stack>
          </motion.div>

          {/* Content Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <ContentContainer
              onClick={handleContentClick}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(convertEmbedUrlsInHtml(step.content), {
                  ALLOWED_TAGS: [
                    'div',
                    'p',
                    'img',
                    'video',
                    'iframe',
                    'br',
                    'strong',
                    'em',
                    'u',
                    'a',
                    'h1',
                    'h2',
                    'h3',
                    'h4',
                    'h5',
                    'h6',
                    'ul',
                    'ol',
                    'li',
                    'span',
                  ],
                  ALLOWED_ATTR: [
                    'src',
                    'alt',
                    'style',
                    'href',
                    'controls',
                    'allowfullscreen',
                    'allow',
                    'loading',
                    'target',
                    'rel',
                    'data-style',
                    'data-variant',
                    'data-color',
                    'data-extension-path',
                    'title',
                    'class',
                  ],
                }),
              }}
            />
          </motion.div>

          {/* Media Section */}
          {mediaUrl && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              {isImageUrl(mediaUrl) ? (
                <MediaImage component="img" src={mediaUrl} alt={step.title} />
              ) : (
                <VideoContainer>
                  {isEmbedVideoUrl(mediaUrl) ? (
                    <Box
                      component="iframe"
                      src={toEmbedUrl(mediaUrl)}
                      title={step.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      sandbox="allow-scripts allow-same-origin allow-presentation"
                      sx={{ border: 0, width: '100%', height: '100%', borderRadius: '18px', display: 'block' }}
                    />
                  ) : (
                    <Box
                      component="video"
                      src={mediaUrl}
                      controls
                      playsInline
                      sx={{ width: '100%', height: '100%', borderRadius: '18px', display: 'block' }}
                    />
                  )}
                </VideoContainer>
              )}
            </motion.div>
          )}

          {/* CTA Button */}
          {showCta && step.ctaAction && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.4 }}
            >
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <CtaButton variant="outlined" onClick={() => onCtaAction?.(step.ctaAction!)} disabled={isBusy}>
                  {step.ctaLabel}
                </CtaButton>
              </motion.div>
            </motion.div>
          )}

          {/* Navigation Buttons */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.4 }}>
            <Stack
              direction={{ xs: 'column-reverse', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
            >
              {showBack ? (
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={{ flex: 1 }}>
                  <BackButton variant="text" onClick={onBack} disabled={isBusy} startIcon={<ArrowBack />}>
                    Back
                  </BackButton>
                </motion.div>
              ) : (
                <Box sx={{ flex: 1 }} />
              )}

              {(step.redirectUrl || (!isLastStep && step.show_continue_button !== false)) && (
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <GradientButton variant="contained" onClick={onNext} disabled={isBusy} endIcon={<ArrowForward />}>
                    Continue
                  </GradientButton>
                </motion.div>
              )}
            </Stack>
          </motion.div>
        </Stack>
      </StepCard>
    </motion.div>
  );
};

export default OnboardingStep;
